import { useState, useEffect, useMemo, useRef } from 'react'
import { useParams, Link } from 'react-router-dom'
import { ArrowLeft, ArrowRight, BookOpen } from 'lucide-react'
import {
  getRelease,
  listReleases,
  recordChangelogVisit,
  type ReleaseNote,
  type ReleaseChange,
  type ReleaseSummary,
} from '../lib/api'
import { ContentSkeleton } from '../components/ui/Skeleton'
import { ErrorState } from '../components/ui/AsyncStates'
import { isNetworkError } from '../lib/api'
import { useRetryOnReconnect } from '../hooks/useOnlineStatus'
import { ImageLightbox } from '../components/ui/ImageLightbox'
import { consumeChangelogUtmSource, readChangelogSource } from '../lib/changelogSource'
import { cn } from '../lib/utils'

const categoryConfig: Record<string, { label: string; color: string; bgColor: string }> = {
  major: { label: 'Major Release', color: 'text-purple-400', bgColor: 'bg-purple-500/15' },
  feature: { label: 'New Feature', color: 'text-blue-400', bgColor: 'bg-blue-500/15' },
  improvement: { label: 'Improvement', color: 'text-green-400', bgColor: 'bg-green-500/15' },
  bugfix: { label: 'Bug Fix', color: 'text-amber-400', bgColor: 'bg-amber-500/15' },
  fix: { label: 'Bug Fix', color: 'text-amber-400', bgColor: 'bg-amber-500/15' },
}

const CATEGORY_ORDER = ['feature', 'improvement', 'bugfix', 'fix', 'major']

function CategoryBadge({ category }: { category: string }) {
  const config = categoryConfig[category] || {
    label: category,
    color: 'text-zinc-400',
    bgColor: 'bg-zinc-500/15',
  }
  return (
    <span className={cn('px-2.5 py-0.5 text-xs font-medium rounded-full', config.bgColor, config.color)}>
      {config.label}
    </span>
  )
}

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

/**
 * Headline feature, single column. Chip + title + description, then a wide
 * inline screenshot directly beneath the copy (Figma / Linear style) — not
 * a tiny thumbnail stranded at the bottom of a card grid.
 */
function Spotlight({ change }: { change: ReleaseChange }) {
  return (
    <article className="py-8 first:pt-0">
      <div className="flex items-center gap-2 mb-2">
        <CategoryBadge category={change.category} />
      </div>
      <h2 className="text-xl font-semibold tracking-tight mb-2">{change.title}</h2>
      {change.description && (
        <p className="text-[15px] leading-relaxed text-muted-foreground max-w-2xl">
          {change.description}
        </p>
      )}
      {change.note && (
        <p className="mt-3 max-w-2xl border-l-2 border-border pl-3 text-[13px] leading-relaxed text-muted-foreground/80">
          <span className="font-medium text-foreground/70">Note</span> — {change.note}
        </p>
      )}
      {change.image && (
        <div className="mt-5 flex justify-center rounded-xl border border-border bg-muted/20 px-3 pb-3 hover:border-primary/40 transition-colors">
          <ImageLightbox
            src={change.image}
            alt={change.title}
            thumbnailClassName="max-h-[340px] w-auto object-contain rounded-md"
          />
        </div>
      )}
    </article>
  )
}

export default function ChangelogDetail() {
  const { slug } = useParams<{ slug: string }>()
  const [release, setRelease] = useState<ReleaseNote | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  // A dropped connection is not a deleted release — see Guide.tsx for the
  // same split.
  const [networkError, setNetworkError] = useState<unknown>(null)
  const [reloadKey, setReloadKey] = useState(0)
  const [allReleases, setAllReleases] = useState<ReleaseSummary[]>([])
  // StrictMode double-mounts useEffect in dev; latch by slug so prev/next
  // navigation still records a new visit but a single mount records once.
  const recordedSlug = useRef<string | null>(null)

  useEffect(() => {
    if (!slug) return
    setLoading(true)

    const shouldRecord = recordedSlug.current !== slug
    const signals = shouldRecord ? readChangelogSource() : null
    if (shouldRecord) {
      recordedSlug.current = slug
      consumeChangelogUtmSource()
    }

    getRelease(slug)
      .then((data) => {
        setRelease(data)
        if (signals) {
          recordChangelogVisit(data.version, signals).catch(() => {})
        }
      })
      .catch((err) => {
        if (isNetworkError(err)) {
          setNetworkError(err)
          return
        }
        setError(err.message || 'Failed to load release')
      })
      .finally(() => setLoading(false))
  }, [slug, reloadKey])

  useRetryOnReconnect(() => {
    if (networkError) setReloadKey((k) => k + 1)
  })

  // Load the version index once so we can render prev/next links.
  useEffect(() => {
    listReleases()
      .then((data) => setAllReleases(data))
      .catch(() => setAllReleases([]))
  }, [])

  // Releases come back newest-first from listReleases (sorted desc by version).
  // Prev = older release (one position later in the array).
  // Next = newer release (one position earlier).
  const { prevRelease, nextRelease } = useMemo(() => {
    if (!release || allReleases.length === 0) {
      return { prevRelease: null as ReleaseSummary | null, nextRelease: null as ReleaseSummary | null }
    }
    const idx = allReleases.findIndex((r) => r.version === release.version)
    if (idx === -1) return { prevRelease: null, nextRelease: null }
    return {
      prevRelease: idx + 1 < allReleases.length ? allReleases[idx + 1] : null,
      nextRelease: idx - 1 >= 0 ? allReleases[idx - 1] : null,
    }
  }, [release, allReleases])

  // Headline features lead the page as spotlights; everything else is a
  // terse list at the bottom so nothing is lost without burying the page.
  const { highlights, rest } = useMemo(() => {
    if (!release) return { highlights: [] as ReleaseChange[], rest: [] as ReleaseChange[] }
    return {
      highlights: release.changes.filter((c) => c.highlight),
      rest: release.changes.filter((c) => !c.highlight),
    }
  }, [release])

  // Group the non-highlighted changes by category so the badge appears once
  // as a section heading (not per row), keeping the tail list compact.
  const restGroups = useMemo(() => {
    const groups = new Map<string, ReleaseChange[]>()
    for (const c of rest) {
      const arr = groups.get(c.category) ?? []
      arr.push(c)
      groups.set(c.category, arr)
    }
    const rank = (c: string) => {
      const i = CATEGORY_ORDER.indexOf(c)
      return i === -1 ? CATEGORY_ORDER.length : i
    }
    return [...groups.entries()].sort((a, b) => rank(a[0]) - rank(b[0]))
  }, [rest])

  if (loading) {
    return (
      <div>
        <Link
          to="/changelog"
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Changelog
        </Link>
        <ContentSkeleton />
      </div>
    )
  }

  if (networkError) {
    return (
      <div>
        <Link
          to="/changelog"
          className="mb-6 flex items-center gap-2 text-muted-foreground transition-colors hover:text-foreground"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Changelog
        </Link>
        <ErrorState error={networkError} onRetry={() => setReloadKey((k) => k + 1)} />
      </div>
    )
  }

  if (error || !release) {
    const isNotFound = error?.toLowerCase().includes('not found')
    return (
      <div>
        <Link
          to="/changelog"
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Changelog
        </Link>
        <p className="text-red-400">
          {isNotFound ? 'Release not found.' : error || 'Release not found.'}
        </p>
      </div>
    )
  }

  return (
    <div className="max-w-3xl mx-auto">
      {/* Back navigation */}
      <Link
        to="/changelog"
        className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-6"
      >
        <ArrowLeft className="w-4 h-4" />
        Back to Changelog
      </Link>

      {/* Release header — no hero image, just the essentials */}
      <header className="mb-2">
        <div className="flex items-center gap-3 mb-3">
          <span className="text-xs font-mono text-muted-foreground">{release.version}</span>
          <span className="text-xs text-muted-foreground">{formatDate(release.date)}</span>
        </div>
        <h1 className="text-3xl font-bold tracking-tight mb-3">{release.title}</h1>
        <p className="text-lg text-muted-foreground leading-relaxed">{release.summary}</p>
      </header>

      {/* Headline features — single-column spotlights with inline screenshots */}
      {highlights.length > 0 && (
        <section className="mt-8 divide-y divide-border">
          {highlights.map((change, i) => (
            <Spotlight key={i} change={change} />
          ))}
        </section>
      )}

      {/* Everything else — grouped by category, badge as section heading, compact */}
      {rest.length > 0 && (
        <section className="mt-12 pt-10 border-t border-border">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-6">
            Also in this release
          </h2>
          <div className="space-y-7">
            {restGroups.map(([category, items]) => (
              <div key={category}>
                <div className="mb-3">
                  <CategoryBadge category={category} />
                </div>
                <ul className="space-y-2">
                  {items.map((change, i) => (
                    <li key={i} className="text-[15px] leading-relaxed">
                      <span className="font-medium">{change.title}</span>
                      {change.description && (
                        <span className="text-muted-foreground"> — {change.description}</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Prev / Next release nav — saves a trip back to the index. */}
      {(prevRelease || nextRelease) && (
        <nav
          aria-label="Release navigation"
          className="mt-12 grid grid-cols-2 gap-3 border-t border-border pt-6"
        >
          {prevRelease ? (
            <Link
              to={`/changelog/${prevRelease.version}`}
              className="group flex items-center gap-3 rounded-lg border border-border bg-card p-4 hover:border-primary/30 transition-colors"
            >
              <ArrowLeft className="w-4 h-4 text-muted-foreground group-hover:text-foreground flex-shrink-0" />
              <div className="min-w-0">
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
                  Previous release
                </div>
                <div className="flex items-baseline gap-2 mt-0.5">
                  <span className="text-xs font-mono text-muted-foreground">
                    {prevRelease.version}
                  </span>
                  <span className="text-sm font-medium truncate">{prevRelease.title}</span>
                </div>
              </div>
            </Link>
          ) : (
            <div />
          )}
          {nextRelease ? (
            <Link
              to={`/changelog/${nextRelease.version}`}
              className="group flex items-center justify-end gap-3 rounded-lg border border-border bg-card p-4 hover:border-primary/30 transition-colors text-right"
            >
              <div className="min-w-0">
                <div className="text-[11px] uppercase tracking-wider text-muted-foreground">
                  Next release
                </div>
                <div className="flex items-baseline gap-2 mt-0.5 justify-end">
                  <span className="text-sm font-medium truncate">{nextRelease.title}</span>
                  <span className="text-xs font-mono text-muted-foreground">
                    {nextRelease.version}
                  </span>
                </div>
              </div>
              <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-foreground flex-shrink-0" />
            </Link>
          ) : (
            <div />
          )}
        </nav>
      )}

      {/* Related guide sections (cross-reference) */}
      {release.guide_sections && release.guide_sections.length > 0 && (
        <div className="mt-8 p-4 rounded-lg bg-secondary/50 border border-border">
          <div className="flex items-start gap-3">
            <BookOpen className="w-4 h-4 text-primary mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-medium text-foreground mb-2">
                Learn more about these features:
              </p>
              <div className="flex flex-wrap gap-2">
                {release.guide_sections.map((guideSlug) => (
                  <Link
                    key={guideSlug}
                    to={`/guide/${guideSlug}`}
                    className="inline-flex items-center px-3 py-1 text-sm rounded-md bg-card border border-border text-primary hover:bg-secondary transition-colors"
                  >
                    {guideSlug
                      .split('-')
                      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
                      .join(' ')}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
